/*
Copyright (c) 2012-2024 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
/** @jsx jsx */
import { jsx, css } from "@emotion/core"
import { useLocaleContext } from "common/utils/i18n"
import { getLocalisedName } from "constants/service_based_menu"
import { Checkbox, Spin } from "@duxton-web/ui"
import useFEConfig from "hooks/useFEConfig"
import { Fragment, useEffect, useMemo, useState } from "react"
import { Item } from "types/models/menu"
import AtLeastOnePurchasabilityAlert from "./AtLeastOnePurchasabilityAlert"

export interface ItemPurchasabilityCheckboxGroupProps {
  item?: Item | null
  onChange?: (
    disabledPurchasabilityKeys: string[],
    metadata?: {
      touched?: boolean
    },
  ) => void
  disabled?: boolean
}

const getDisabledPurchasabilities = (
  purchasabilityKeyChoices: string[],
  selectedPurchasabilities: string[],
) => {
  const selectedPurchasabilitiesSet = new Set<string>()
  selectedPurchasabilities.forEach(e => selectedPurchasabilitiesSet.add(e))
  return (
    purchasabilityKeyChoices?.filter(
      e => !selectedPurchasabilitiesSet.has(e),
    ) ?? []
  )
}

function ItemPurchasabilityCheckboxGroup({
  item,
  onChange,
  disabled,
}: ItemPurchasabilityCheckboxGroupProps) {
  const feConfig = useFEConfig()
  const { disallowedSOCKeys } = feConfig
  const locale = useLocaleContext("locale")
  const [selectedPurchasabilities, setSelectedPurchasabilities] = useState<
    string[]
  >()
  const purchasabilityConfig =
    feConfig.data?.serviceBasedMenuConfig?.characteristicStrategy
      ?.purchasableConfig
  // select all purchasability by default
  const purchasabilityKeyChoices = useMemo(
    () => purchasabilityConfig?.map(config => config.key),
    [purchasabilityConfig],
  )
  const [
    displayAtLeastOnePurchasabilityAlert,
    setDisplayAtLeastOnePurchasabilityAlert,
  ] = useState(false)

  // effect for setting item purchasability if they haven't been set before
  useEffect(() => {
    // if item or defaultSelectedPurchasabilityKeys has changed, reset touched state
    if (!purchasabilityKeyChoices) {
      // there's no point, because purchasabilityConfig has not been loaded yet
      return
    }
    const itemPurchasability = item?.purchasability
    // get list of purchasability keys that are disabled (by default all of them are enabled)
    const disabledPurchasabilityKeySet = new Set()
    Object.entries(itemPurchasability ?? {})
      .filter(([, val]) => !val)
      .forEach(([key]) => disabledPurchasabilityKeySet.add(key))
    const newSelectedPurchasabilities = purchasabilityKeyChoices.filter(
      e => !disabledPurchasabilityKeySet.has(e), // if the set has them, then it's disabled
    )
    setSelectedPurchasabilities(newSelectedPurchasabilities)
  }, [item, purchasabilityKeyChoices])

  useEffect(() => {
    if (purchasabilityKeyChoices && selectedPurchasabilities) {
      // if the default keys have been initialised
      onChange?.(
        getDisabledPurchasabilities(
          purchasabilityKeyChoices,
          selectedPurchasabilities,
        ),
      )
    }
  }, [selectedPurchasabilities, onChange, purchasabilityKeyChoices])

  useEffect(() => {
    if (displayAtLeastOnePurchasabilityAlert) {
      const hidePurchasabilityAlert = () =>
        setDisplayAtLeastOnePurchasabilityAlert(false)
      const timeoutHandle = setTimeout(hidePurchasabilityAlert, 3000)
      return () => clearTimeout(timeoutHandle)
    }
    return () => {}
  }, [displayAtLeastOnePurchasabilityAlert])

  if (feConfig.isLoading) {
    return <Spin />
  }
  return (
    <Fragment>
      <AtLeastOnePurchasabilityAlert
        visible={displayAtLeastOnePurchasabilityAlert}
      />
      <Checkbox.Group
        data-testid="purchasability-group"
        value={selectedPurchasabilities}
        disabled={disabled}
        onChange={(e: string[]) => {
          if (e.length && purchasabilityKeyChoices) {
            setSelectedPurchasabilities(e)
            onChange?.(
              getDisabledPurchasabilities(purchasabilityKeyChoices, e),
              { touched: true },
            )
          } else {
            setDisplayAtLeastOnePurchasabilityAlert(true)
          }
        }}
        css={css`
          display: flex;
          flex-direction: column;
        `}
      >
        {purchasabilityConfig?.map(config => (
          <div
            key={config.key}
            css={css`
              margin-bottom: 20px;
            `}
          >
            <Checkbox value={config.key}>
              {getLocalisedName(config, locale, disallowedSOCKeys)}
            </Checkbox>
          </div>
        ))}
      </Checkbox.Group>
    </Fragment>
  )
}

export default ItemPurchasabilityCheckboxGroup
